import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { User } from '../types/auth.types';
import { tokenManager } from '../utils/tokenManager';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (user: User, token: string) => void;
  logout: () => void;
  setUser: (user: User) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  initAuth: () => void;
}

type AuthStore = AuthState & AuthActions;

// Hydrate from localStorage synchronously so first render sees correct auth state (avoids redirect on refresh)
function getInitialAuthState(): Pick<AuthState, 'user' | 'isAuthenticated'> {
  const user = tokenManager.getUser<User>();
  return {
    user: user ?? null,
    isAuthenticated: !!user,
  };
}

const initialState = getInitialAuthState();

export const useAuthStore = create<AuthStore>()(
  devtools(
    immer((set) => ({
      // State (hydrated from localStorage so refresh keeps user logged in)
      user: initialState.user,
      isAuthenticated: initialState.isAuthenticated,
      isLoading: false,
      error: null,

      // Actions
      login: (user, token) => {
        tokenManager.setToken(token);
        tokenManager.setUser(user);

        set((state) => {
          state.user = user;
          state.isAuthenticated = true;
          state.error = null;
        });
      },

      logout: () => {
        tokenManager.clearTokens();
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.error = null;
        });
      },

      setUser: (user) => {
        tokenManager.setUser(user);
        set((state) => {
          state.user = user;
        });
      },

      setLoading: (isLoading) => {
        set((state) => {
          state.isLoading = isLoading;
        });
      },

      setError: (error) => {
        set((state) => {
          state.error = error;
        });
      },

      initAuth: () => {
        const user = tokenManager.getUser<User>();

        // Restore auth when we have user in storage. Session-based auth stores
        // empty token; token-based auth stores a real token. Either case counts as authenticated.
        if (user) {
          set((state) => {
            state.user = user;
            state.isAuthenticated = true;
          });
        }
      },
    })),
    { name: 'AuthStore' }
  )
);
